/**
 * Breaking News page — high-importance events pulled from
 * `/events/breaking` (importance ≥ 80). Rendered with the same card
 * grid as Latest News so the visual language is consistent.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { npApi } from "./api";
import { timeAgo, categoryLabel, primaryCategoryPath } from "./theme";

export default function NewsPulseBreaking() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    npApi.breaking(50)
      .then((r) => { if (!cancelled) setEvents(r); })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid="newspulse-breaking-page">
      <div className="border-b border-slate-200 pb-4 mb-8 flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
          <Radio className="w-3.5 h-3.5" /> Live
        </span>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Breaking News</div>
          <h1 className="text-3xl font-bold text-slate-900">
            High-Impact Stories Right Now
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/3] rounded-lg bg-slate-200" />
              <div className="h-4 w-full bg-slate-200 rounded mt-3" />
              <div className="h-3 w-1/2 bg-slate-200 rounded mt-2" />
            </div>
          ))}
        </div>
      ) : err ? (
        <p className="text-red-600">Error: {err}</p>
      ) : events.length === 0 ? (
        <p className="text-slate-500 italic">
          No breaking stories at the moment — check back later.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e) => {
            const cat = primaryCategoryPath(e.categories);
            return (
              <Link
                key={e.event_id}
                to={`/event/${e.event_id}`}
                className="group block bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`newspulse-breaking-card-${e.event_id}`}
              >
                <div className="aspect-[16/9] bg-slate-100 relative">
                  {e.image_url ? (
                    <img
                      src={e.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                      <Radio className="w-8 h-8 text-white/60" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                    Breaking
                  </span>
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600">
                    {categoryLabel(e.categories) || "News"}
                  </div>
                  <h3 className="mt-1 text-base font-semibold leading-snug text-slate-900 group-hover:text-blue-700 line-clamp-3">
                    {e.headline}
                  </h3>
                  {e.summary && (
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {e.summary}
                    </p>
                  )}
                  <div className="mt-3 text-xs text-slate-500">
                    {timeAgo(e.last_updated_at)} · importance {e.importance}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
